import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';
import { VideoData } from '@lumen5/luminary';

import { z } from 'zod';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import { MediaRegistry } from '../registries/MediaRegistry';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';
import type { ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import { setFullMediaTranslation } from 'js/src/agent/video-state';
import BackgroundAsset from 'js/src/components/app/BackgroundAsset';
import { setBrightnessUserOverride, setCropUserOverride } from 'js/src/data/user-overrides';
import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import { saveRawLuminary } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    applyUserMediaKenburnOperation,
    fetchAndSetRecommendedSceneAttributesOperation,
} from 'js/src/luminary/luminary-operations';

export const legacySetMediaSchema = z.object({
    scene_index: z.number().describe('The scene index (0, 1, 2, etc.)'),
    asset_name: z.string().describe('The asset name (e.g. media_1)'),
    media_id: z
        .string()
        .optional()
        .describe('The ID of the media to apply to the asset. Must be an ID from search results.'),
    volume: z
        .number()
        .min(0)
        .max(100)
        .optional()
        .describe('Volume level for video assets, from 0 (silent) to 100 (full)'),
    brightness: z
        .number()
        .min(0)
        .max(1.5)
        .optional()
        .describe(
            'Brightness level for the media asset (0=completely black, 0.5=dimmed, 1=default, 1.2=bright, 1.5=very bright)',
        ),
    position: z
        .object({
            x: z.number().meta({
                description:
                    'X value in pixels. To move the media to the left, you MUST use a positive value (for example: 10). To move the media to the right, you MUST use a negative value (for example: -10).',
                default: 0,
            }),
            y: z.number().meta({
                description:
                    'Y value in pixels. To move the media up, you MUST use a positive value (for example: 15). To move the media down (bottom), you MUST use a negative value (for example: -15).',
                default: 0,
            }),
        })
        .optional()
        .describe(
            "Translate the media relative to the center of the asset's bounds. The translation range is determined by the asset's mediaXTranslationRange and mediaYTranslationRange property. If the range is too narrow, increase the media's scale.",
        ),
    scale: z
        .number()
        .min(10)
        .max(1000)
        .optional()
        .describe(
            'Scale value for the media in percentage. To increase the size (width and height) of the media, you MUST use a value greater than 100.',
        ),
    kenburn: z
        .enum(VideoData.ALL_BACKGROUND_ANIMATIONS)
        .optional()
        .describe('Ken Burns animation effect for the media. Use "none" to disable animation.'),
});
export type SetMediaArgs = z.infer<typeof legacySetMediaSchema>;

export interface SetMediaToolConstructorArgs extends ToolConstructorArgs {
    /**
     * When true (default), changing a scene's media fetches and applies recommended
     * scene attributes (crop, brightness, Ken Burns, etc.) inline, matching a manual
     * media swap. Set to false for bulk flows like the Media Optimizer, which apply
     * recommendations in a single batch at the end of the run to avoid spawning a
     * WebGL context per scene (which can cause context-loss errors).
     */
    recommendSceneAttributes?: boolean;
}

export class LegacySetMediaTool extends VideoStateTool<
    typeof legacySetMediaSchema,
    SetMediaToolConstructorArgs
> {
    protected override readonly schema = legacySetMediaSchema;
    protected override readonly toolName = TOOL_NAMES.setMedia;
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    protected override readonly toolDescription = `Set media, position, scale or volume for a specific scene and asset.

Scaling the media will increase the translation range.
When translating media, you MUST evaluate the use of both the mediaXTranslationRange and mediaYTranslationRange properties.

<example>
When asked, move the media up when the mediaXTranslationRange is [-50, 50] and mediaYTranslationRange is [-25, 25].
Call ${TOOL_NAMES.setMedia} position with { x: 0, y: 25 }.
</example>

<example>
When asked, move the media down when the mediaXTranslationRange is [-50, 50] and mediaYTranslationRange is [-25, 25].
Call ${TOOL_NAMES.setMedia} position with { x: 0, y: -25 }.
</example>

<example>
When asked, move the media down right when the mediaXTranslationRange is [-50, 50] and mediaYTranslationRange is [-25, 25].
Call ${TOOL_NAMES.setMedia} position with { x: -50, y: -25 }.
Call ${TOOL_NAMES.getSceneScreenshotWithDescription} and verify that the result matches the expected change
Call ${TOOL_NAMES.setMedia} and ${TOOL_NAMES.getSceneScreenshotWithDescription} until we achieve the desired result
...
</example>

<example>
When asked, make an object larger and move it to the top left
Call ${TOOL_NAMES.setMedia} scale with 150.
Call ${TOOL_NAMES.setMedia} position with { x: 50, y: 50 }.
Call ${TOOL_NAMES.getSceneScreenshotWithDescription} and verify that the result matches the expected change
Call ${TOOL_NAMES.setMedia} and ${TOOL_NAMES.getSceneScreenshotWithDescription} until we achieve the desired result
...
</example>
`;

    public override customRenderer = SetMediaResultRenderer;

    private readonly recommendSceneAttributes: boolean;

    public constructor(args: SetMediaToolConstructorArgs) {
        super(args);
        this.recommendSceneAttributes = args.recommendSceneAttributes ?? true;
    }

    public override getToolDisplayName(): string {
        return 'Setting media';
    }

    protected override onEditApplied(args: SetMediaArgs): void {
        // Changing media on the first scene changes the thumbnail.
        if (args.scene_index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'image';
    }

    protected override preprocessArgs(args: SetMediaArgs): SetMediaArgs {
        // position.x/y are required-with-default(0) inside the optional position object.
        // .meta({ default }) emits the default but does not fill it at parse time (unlike the
        // legacy Ajv useDefaults path), so a partial position like { x: 5 } would hard-fail on the
        // missing y. Fill the missing axis with 0 here (before validation) to preserve the old
        // behavior — the emitted schema is unchanged.
        if (args.position) {
            // The arg type says position is complete, but at parse time it may be partial, so cast
            // to optional axes to make the 0-defaults meaningful.
            const position = args.position as { x?: number; y?: number };
            args = { ...args, position: { x: 0, y: 0, ...position } };
        }
        return args;
    }

    public async execute(args: SetMediaArgs, context: ToolExecutionContext) {
        const { scene_index, asset_name, media_id, volume, brightness, position, scale, kenburn } =
            args;
        const { parentActionGroup } = context;

        const changes: string[] = [];
        let mediaChanged = false;
        try {
            if (scene_index >= this.luminary.scenes.length) {
                return `Error: scene_index ${scene_index} is out of bounds for the current video`;
            }
            const scene = this.luminary.scenes[scene_index];
            if (!scene) {
                return `Error: No scene found at index ${scene_index}`;
            }
            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${scene_index} which uses design ${scene.sceneDesign}`;
            }
            if (!asset.isEditable) {
                return `Error: Asset ${asset_name} in scene ${scene_index} is not editable`;
            }

            if (!VideoData.isMediaAsset(asset)) {
                return `Error: Asset ${asset_name} in scene ${scene_index} is not a media asset`;
            }

            // Resolve (but don't yet apply) the new media so we can validate it up
            // front and apply the swap LAST — after the explicit Ken Burns /
            // brightness overrides below are registered. Swapping media triggers a
            // scene-attribute recommendation that only leaves an attribute alone if
            // the user has overridden it, so the overrides must exist first.
            let mediaSourceData: ReturnType<MediaRegistry['getMediaSourceData']> = null;
            if (media_id !== undefined) {
                const mediaRegistry = MediaRegistry.getInstance();
                if (!mediaRegistry.hasItem(media_id)) {
                    return `Error: Unknown media ${media_id}`;
                }
                mediaSourceData = mediaRegistry.getMediaSourceData(media_id);
                if (!mediaSourceData) {
                    return `Error: Unknown media ${media_id}`;
                }
            }

            const { applyUserMediaKenburn, fetchAndSetRecommendedSceneAttributes } =
                makeOperationsCallable(
                    {
                        applyUserMediaKenburnOperation,
                        fetchAndSetRecommendedSceneAttributesOperation,
                    },
                    this.luminary,
                    this.dispatch,
                    this._getState,
                );

            // Explicit Ken Burns wins: set the value AND register the user override
            // so the media-swap recommendation below (and any future swap or
            // scene-design change) leaves it alone. All dispatches share
            // parentActionGroup, so they collapse into a single undo checkpoint.
            if (kenburn !== undefined) {
                applyUserMediaKenburn(scene.id, asset.id, kenburn, false, parentActionGroup);
                changes.push(`media animation to ${kenburn}`);
            }

            // Explicit brightness wins the same way. Register the override
            // synchronously here — the dedicated brightness operation debounces its
            // override dispatch, which would land after the recommendation has
            // already read the override state.
            if (brightness !== undefined) {
                asset.brightness = brightness;
                this.dispatch(
                    setBrightnessUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
                changes.push(`media brightness to ${brightness}`);
            }

            // Swap media before applying scale/position. Swapping sourceData resets
            // crop/scale/flip transformations (but NOT kenburn, so the value set
            // above survives), so any explicit scale/position must be applied AFTER
            // the swap or they would be silently wiped. The scene-attribute
            // recommendation below runs with autoScale: false, so it won't restore
            // them either.
            if (mediaSourceData) {
                try {
                    asset.sourceData = mediaSourceData;
                } catch (error) {
                    return `Error: data wrong format for asset ${asset_name} in scene ${scene_index}.`;
                }
                changes.push(`set media for ${asset_name} to ${media_id}`);
                mediaChanged = true;
            }

            // Volume is checked against the media the asset holds AFTER the swap: a video replacing
            // an image can carry it, and volume on an image is not worth rejecting the whole call
            // over — skip it and say so, so the other edits still land.
            let volumeNote = '';
            if (volume !== undefined) {
                if (asset.isVideo) {
                    asset.volume = volume;
                    changes.push(`media volume to ${volume}`);
                } else {
                    volumeNote = ` NOTE: volume was ignored because ${asset_name} contains an image, which has no audio.`;
                }
            }

            // Apply scale before position, as scale affects the translation range.
            if (scale !== undefined) {
                const scaleValue = scale / 100;
                asset.scaleX = scaleValue;
                asset.scaleY = scaleValue;
                changes.push('media scale');
            }
            if (position !== undefined) {
                setFullMediaTranslation(asset, position.x, position.y);

                const xDirection = position.x === 0 ? 'center' : position.x < 0 ? 'right' : 'left';
                const yDirection = position.y === 0 ? 'center' : position.y < 0 ? 'down' : 'up';
                changes.push(
                    `media position to ${position.x} (${xDirection}), ${position.y} (${yDirection})`,
                );
            }

            // Scale and position are crop transformations. Explicit values win:
            // register the crop user override so the media-swap recommendation
            // below leaves them alone — it otherwise resets crop (including scale,
            // via setMediaCrop's default) unless the user has overridden it.
            if (scale !== undefined || position !== undefined) {
                this.dispatch(
                    setCropUserOverride({
                        sceneId: scene.id,
                        isAutomatic: false,
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            if (mediaChanged && this.recommendSceneAttributes) {
                // Fetch and apply recommended scene attributes (crop, brightness,
                // Ken Burns, text position, watermark contrast) for the changed
                // media, matching what happens on a manual media swap. Attributes
                // the user set explicitly above are marked as overridden, so the
                // recommendation leaves those alone. This operation checkpoints the
                // video itself.
                await fetchAndSetRecommendedSceneAttributes({
                    sceneId: scene.id,
                    autoCrop: true,
                    recommendBrightness: true,
                    systemTriggered: true,
                    parentActionGroup,
                    autoScale: false,
                });
            } else if (changes.length > 0) {
                // Only non-media properties changed, or recommendations are applied
                // in batch by the caller (e.g. the Media Optimizer). Checkpoint the
                // final state here; sharing parentActionGroup groups it with any
                // override dispatches above into a single undo step.
                this.dispatch(
                    checkpointVideo({
                        video_data: saveRawLuminary(this.luminary),
                        systemTriggered: false,
                        parentActionGroup,
                    }),
                );
            }

            if (changes.length === 0) {
                return `Success: Nothing was changed for asset ${asset_name} in scene ${scene_index}.${volumeNote}`;
            }

            this.recordSceneFill(scene_index, 'media');

            return `Success: Updated ${changes.join(', ')} for asset ${asset_name} in scene ${scene_index}${volumeNote}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting media: ${errorMessage}`;
        }
    }
}

const SetMediaResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
    toolCall,
}) => {
    if (isLoading) {
        return null;
    }
    const args = toolCall ? JSON.parse(toolCall.function.arguments) : {};
    const mediaId = args.media_id;
    const mediaRegistry = MediaRegistry.getInstance();
    const mediaData = mediaId ? mediaRegistry.getItem(mediaId) : null;
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
            {mediaData && (
                <div>
                    {React.createElement(BackgroundAsset, {
                        searchContext: null,
                        media: mediaData,
                        media_id: 0,
                        result_index: 0,
                    })}
                </div>
            )}
        </div>
    );
};
